import React, { useState } from 'react';
import { Logo } from '@components/Logo/Logo';
import { MenuOutlined } from '@ant-design/icons';
import { Button, Drawer, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { HashLink } from 'react-router-hash-link';
import { AppEnvVariables } from '@utils/AppEnvVariables';

export const HeaderSection = () => {
  const [open, setOpen] = useState(false);

  const { i18n, t } = useTranslation();

  const currentLang = i18n.language.split(/[-_]/)[0];

  const menuItems = [
    { link: '/#home', text: t('header.home') },
    { link: '/#features', text: t('common.features') },
    { link: '/#pricing', text: t('header.pricing') },
    { link: '/legal', text: t('common.legal') },
    { link: '/#contact', text: t('common.contact') },
    {
      external: true,
      link: AppEnvVariables.getCustomerDashboardUrl(),
      text: t('common.login'),
    },
  ];

  const languageOptions = [
    {
      label: (
        <span className="flex items-center gap-2">
          🇺🇸 <span>EN</span>
        </span>
      ),
      value: 'en',
    },
    {
      label: (
        <span className="flex items-center gap-2">
          🇪🇸 <span>ES</span>
        </span>
      ),
      value: 'es',
    },
    {
      label: (
        <span className="flex items-center gap-2">
          🇧🇷 <span>PT</span>
        </span>
      ),
      value: 'pt',
    },
    {
      label: (
        <span className="flex items-center gap-2">
          🇷🇺 <span>RU</span>
        </span>
      ),
      value: 'ru',
    },
  ];

  const handleLanguageChange = (value: string) => {
    i18n.changeLanguage(value).then();
  };

  return (
    <header
      className="fixed top-8 inset-x-4 md:inset-x-10 lg:inset-x-[40px]
             z-50 w-auto h-[72.12px] bg-white rounded-[32px]
             px-4 md:px-[40px] py-[16px]
             flex items-center justify-between
             shadow-[0px_-19px_40.4px_-19px_#D0E3ED]"
    >
      <Logo />

      <nav className="hidden md:flex gap-4 text-sm font-medium">
        <Select
          value={currentLang}
          onChange={handleLanguageChange}
          size="small"
          variant="borderless"
          className="border-none bg-transparent text-sm text-gray-700 hover:text-blue-500 focus:ring-0 focus:outline-none w-[90px] p-0"
          options={languageOptions}
        />
        {menuItems.map((item) =>
          item.external ? (
            <a
              key={item.text}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-blue-500 transition"
            >
              {item.text}
            </a>
          ) : (
            <HashLink
              smooth
              to={item.link}
              key={item.text}
              className="hover:text-blue-500 transition"
            >
              {item.text}
            </HashLink>
          )
        )}
      </nav>

      <Button
        className="md:hidden border-none shadow-none text-xl"
        type="text"
        icon={<MenuOutlined />}
        onClick={() => setOpen(true)}
      />

      <Drawer
        title="Menu"
        placement="right"
        onClose={() => setOpen(false)}
        open={open}
        className="md:hidden"
      >
        <nav className="flex flex-col gap-4 text-base font-medium">
          {menuItems.map((item) => (
            <HashLink
              smooth
              to={item.link}
              key={item.text}
              onClick={() => setOpen(false)}
              className="hover:text-blue-500 transition"
            >
              {item.text}
            </HashLink>
          ))}
          <div className="flex justify-start items-center gap-4 border-t border-blue-100 pt-4">
            {languageOptions.map((lang) => (
              <button
                type="button"
                onClick={() => handleLanguageChange(lang.value)}
                className={`text-sm font-semibold uppercase transition-colors duration-200 ${
                  lang.value === currentLang
                    ? 'text-blue-700'
                    : 'text-blue-300 hover:text-blue-500'
                }`}
              >
                {lang.label}
              </button>
            ))}
          </div>
        </nav>
      </Drawer>
    </header>
  );
};
